import type { FocusAssetId, PersonaId, PlatformModuleId } from '@/types'

export interface SnapshotCard {
  label: string
  value: string
  change: string
  positive: boolean
}

export interface DeskSignal {
  label: string
  value: string
  tone: string
}

export interface LensModule {
  summary: string
  monitors: string[]
  automations: string[]
  pulse: Array<{
    label: string
    value: string
    tone: string
  }>
}

export interface WorkbenchProfile {
  deskLabel: string
  objective: string
  summary: string
  dashboardHeadline: string
  dashboardSummary: string
  dashboardNotes: string[]
  moduleOrder: PlatformModuleId[]
  snapshots: SnapshotCard[]
  signals: DeskSignal[]
}

type ProfilePreset = WorkbenchProfile

export const PERSONAS_BY_ASSET: Record<FocusAssetId, PersonaId[]> = {
  enterprise: ['executive', 'portfolio-manager', 'risk-manager'],
  cre: ['originator', 'asset-manager', 'portfolio-manager', 'secondary-trader', 'risk-manager'],
  cmbs: ['secondary-trader', 'structurer', 'risk-manager', 'portfolio-manager'],
  rmbs: ['secondary-trader', 'structurer', 'risk-manager', 'portfolio-manager'],
  clo: ['secondary-trader', 'structurer', 'risk-manager', 'portfolio-manager'],
  'whole-loans': ['originator', 'secondary-trader', 'asset-manager', 'portfolio-manager', 'risk-manager'],
}

export const ASSET_META: Record<FocusAssetId, { label: string; code: string; description: string }> = {
  enterprise: {
    label: 'Enterprise',
    code: 'ENT',
    description: 'Cross-asset command view spanning origination, structuring, surveillance, trading, and reporting.',
  },
  cre: {
    label: 'Commercial Real Estate',
    code: 'CRE',
    description: 'Direct CRE lending, asset management, securitization preparation, and portfolio surveillance.',
  },
  cmbs: {
    label: 'CMBS',
    code: 'CMBS',
    description: 'Conduit and SASB pooling, tranche construction, secondary bond execution, and surveillance.',
  },
  rmbs: {
    label: 'RMBS',
    code: 'RMBS',
    description: 'Borrower tape analytics, prepay and delinquency monitoring, and mortgage bond execution.',
  },
  clo: {
    label: 'CLO',
    code: 'CLO',
    description: 'Warehouse management, tranche tests, collateral quality, and structured credit trading.',
  },
  'whole-loans': {
    label: 'Whole Loans',
    code: 'WL',
    description: 'Loan acquisition, tape bidding, carry monitoring, exit strategy, and buyer coverage.',
  },
}

export const PERSONA_META: Record<PersonaId, { label: string; shortLabel: string; description: string }> = {
  executive: {
    label: 'Executive',
    shortLabel: 'CEO',
    description: 'Enterprise-level allocation, capital formation, and strategic performance oversight.',
  },
  originator: {
    label: 'Originator',
    shortLabel: 'ORIG',
    description: 'Pipeline development, pricing, sponsor coverage, and execution throughput.',
  },
  'asset-manager': {
    label: 'Asset Manager',
    shortLabel: 'AM',
    description: 'Portfolio health, covenant protection, NOI durability, and servicing actions.',
  },
  'portfolio-manager': {
    label: 'Portfolio Manager',
    shortLabel: 'PM',
    description: 'Long-duration yield defense, allocation, and risk-adjusted portfolio performance.',
  },
  'secondary-trader': {
    label: 'Secondary Trader',
    shortLabel: 'TRD',
    description: 'Price action, liquidity, position history, relative value, and exits.',
  },
  'risk-manager': {
    label: 'Risk Manager',
    shortLabel: 'RISK',
    description: 'Downside containment, trigger governance, concentration, and adverse scenario control.',
  },
  structurer: {
    label: 'Structurer',
    shortLabel: 'STR',
    description: 'Pool engineering, tranche economics, ratings dialogue, and capital stack optimization.',
  },
}

export const MODULE_META: Record<PlatformModuleId, { label: string; short: string; route: string; description: string }> = {
  // Executive boards. Every PlatformModuleId must appear here — the workbench
  // reads MODULE_META[moduleId].label unguarded, so a missing id is a runtime
  // TypeError, not a blank cell. Copy stays in step with WORKFLOW_META.
  'dashboard-legal': {
    label: 'Risk Mgmt',
    short: 'RSK',
    route: '/dashboards/legal',
    description: 'Matters, obligations, exceptions, audits, KYC and counsel spend in one briefing.',
  },
  'dashboard-underwriting': {
    label: 'Underwriting Mgr',
    short: 'UWM',
    route: '/dashboards/underwriting',
    description: 'Desk queue, stage ageing, diligence completeness, and model readiness.',
  },
  'dashboard-asset': {
    label: 'Asset Mgr',
    short: 'AMG',
    route: '/dashboards/asset',
    description: 'Portfolio tiering, covenant test results, borrower requests, and maturities.',
  },
  'dashboard-ceo': {
    label: 'CEO',
    short: 'CEO',
    route: '/dashboards/ceo',
    description: 'Production against plan, credit quality, capital deployment, and relationships.',
  },
  'golden-deal-reconciliation': {
    label: 'Golden-Deal Recon',
    short: 'GDR',
    route: '/golden-deal-reconciliation',
    description: 'Golden deals run through the platform against the client’s own credit decisions.',
  },
  'credit-box': {
    label: 'Credit Box',
    short: 'CBX',
    route: '/credit-box',
    description: 'The versioned ruleset every deal is screened against.',
  },
  'command-center': {
    label: 'Command Center',
    short: 'CMD',
    route: '/command-center',
    description: 'Cross-portfolio control tower — pipeline pressure, KPIs, triggers, and funding readiness.',
  },
  'db-search': {
    label: 'DB Search',
    short: 'DBS',
    route: '/db-search',
    description: 'Every deal since inception in one query — filter, aggregate, compare periods, export to Excel.',
  },
  pipeline: {
    label: 'Pipeline',
    short: 'PIP',
    route: '/pipeline',
    description: 'Full deal pipeline with filtering, sorting, and deal-level detail panels.',
  },
  intake: {
    label: 'Intake',
    short: 'INT',
    route: '/intake',
    description: 'Broker/borrower request ingestion, triage, and initial deal creation.',
  },
  screening: {
    label: 'Screening',
    short: 'SCR',
    route: '/screening',
    description: 'Deal sizing, mandate fit analysis, and kill-or-advance decisions.',
  },
  underwriting: {
    label: 'Underwriting',
    short: 'U/W',
    route: '/underwriting',
    description: 'Cash flow analysis, stress testing, structure, and covenant shaping.',
  },
  committee: {
    label: 'Committee',
    short: 'COM',
    route: '/committee',
    description: 'Credit memo production, approver routing, and conditions precedent.',
  },
  closing: {
    label: 'Closing',
    short: 'CLS',
    route: '/closing',
    description: 'Legal, rate lock, checklist tracking, fee calculation, and wire release.',
  },
  surveillance: {
    label: 'Surveillance',
    short: 'SRV',
    route: '/surveillance',
    description: 'Covenant compliance, watchlists, trigger management, and borrower reporting.',
  },
  'asset-management': {
    label: 'Asset Mgmt',
    short: 'A/M',
    route: '/asset-management',
    description: 'Development feasibility, DCF analysis, budget tracking, and portfolio consolidation.',
  },
  valuation: {
    label: 'Valuation',
    short: 'VAL',
    route: '/valuation',
    description: 'Property valuation management, benchmarking, and performance attribution.',
  },
  'market-intelligence': {
    label: 'Market Intel',
    short: 'MKT',
    route: '/market-intel',
    description: 'External model comparison, market data feeds, and risk overlays.',
  },
  'market-news': {
    label: 'Market News',
    short: 'NWS',
    route: '/market-news',
    description: 'Real-time market news, alerts, and entity tracking.',
  },
  crm: {
    label: 'CRM',
    short: 'CRM',
    route: '/crm',
    description: 'Client relationship management, contacts, and deal origination tracking.',
  },
  reports: {
    label: 'Reports',
    short: 'RPT',
    route: '/reports',
    description: 'ASR generation, scheduled reports, portfolio summaries, and investor packages.',
  },
  'loan-comparison': {
    label: 'Loan Compare',
    short: 'CMP',
    route: '/loan-comparison',
    description: 'Side-by-side comparison of deal structures with stress testing and guideline checks.',
  },
  'portfolio-analytics': {
    label: 'Analytics',
    short: 'ANL',
    route: '/portfolio-analytics',
    description: 'Risk heatmaps, concentration analysis, performance attribution, and conversion funnels.',
  },
  'term-sheet': {
    label: 'Term Sheets',
    short: 'T/S',
    route: '/term-sheet',
    description: 'Auto-generate professional term sheets from deal data with live preview.',
  },
  'hedge-desk': {
    label: 'Hedge Desk',
    short: 'HDG',
    route: '/hedge-desk',
    description: 'Floating rate exposure tracking, interest rate cap modeling, and hedge analytics.',
  },
  'document-vault': {
    label: 'Documents',
    short: 'DOC',
    route: '/documents',
    description: 'Centralized document vault with version control and closing checklist linkage.',
  },
  'investor-portal': {
    label: 'Investors',
    short: 'INV',
    route: '/investor-portal',
    description: 'Read-only LP portal showing portfolio NAV, distributions, investment performance, and quarterly reports.',
  },
  'borrower-portal': {
    label: 'Borrowers',
    short: 'BRW',
    route: '/borrower-portal',
    description: "Staff view of each borrower's application workspace: outstanding documents, uploads and loan tracking. Staff sign-in required; borrowers cannot sign in yet.",
  },
  'bi-dashboard': {
    label: 'BI Dashboard',
    short: 'BI',
    route: '/bi-dashboard',
    description: 'Deep analytics with origination volume, conversion funnels, originator scorecards, and revenue attribution.',
  },
  'comp-database': {
    label: 'Comp DB',
    short: 'CDB',
    route: '/comp-database',
    description: 'Internal comparable transaction database that grows with every deal.',
  },
  'workflow-automation': {
    label: 'Automation',
    short: 'AUT',
    route: '/automation',
    description: 'Configurable automation triggers with no-code rule builder.',
  },
}

const PROFILE_PRESETS: Record<FocusAssetId, Partial<Record<PersonaId, ProfilePreset>>> = {
  enterprise: {
    executive: {
      deskLabel: 'Enterprise Command',
      objective: 'Allocate capital across origination, securitization, surveillance, and distribution from one operating surface.',
      summary: 'Surface revenue momentum, funding bottlenecks, concentration risk, and investor cadence at the same time.',
      dashboardHeadline: 'Cross-asset finance operations with capital deployment, securitization, surveillance, and reporting on one premium terminal.',
      dashboardSummary: 'Use the enterprise lens to orchestrate each desk without losing asset-specific detail. The shell elevates capital velocity, risk drift, and production readiness first.',
      dashboardNotes: [
        'Capital formation, production, and surveillance are stacked together so leadership can spot drift before it erodes margin.',
        'Origination and secondary execution are visible in one frame to expose where capital is created versus merely carried.',
        'Committee, investor, and servicing outputs stay tied to the same operating data so reporting does not lag the book.',
      ],
      moduleOrder: ['command-center', 'reports', 'asset-management', 'intake', 'screening', 'underwriting', 'committee', 'market-intelligence', 'closing', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Enterprise Volume', value: '$4.82B', change: '+6.4%', positive: true },
        { label: 'Watchlist Drift', value: '18 Names', change: '-3', positive: true },
        { label: 'Capital Velocity', value: '41 Days', change: '-2.1d', positive: true },
      ],
      signals: [
        { label: 'Leadership View', value: 'LIVE', tone: 'text-emerald-400' },
        { label: 'Funding Window', value: 'OPEN', tone: 'text-amber-300' },
        { label: 'Risk Envelope', value: 'CONTROLLED', tone: 'text-stone-300' },
      ],
    },
    'portfolio-manager': {
      deskLabel: 'Enterprise PM View',
      objective: 'Defend long-duration yield and capital preservation across asset classes and execution channels.',
      summary: 'Emphasize carry durability, exposure migration, and refinancing vulnerability ahead of production vanity metrics.',
      dashboardHeadline: 'Portfolio-first command coverage with yield defense, concentration control, and refinance risk elevated above pure volume.',
      dashboardSummary: 'The workbench now leads with surveillance, forward yield, and liquidity transfer risk so the portfolio can be managed for durability rather than headline flow.',
      dashboardNotes: [
        'Watchlist pressure and cash flow drift are placed ahead of issuance optics because yield loss compounds faster than volume helps.',
        'Asset-level underperformance, securitized spread moves, and maturity walls are grouped in one frame for allocation decisions.',
        'The shell stays cross-asset, but the module order pushes monitoring and reporting ahead of new production screens.',
      ],
      moduleOrder: ['asset-management', 'command-center', 'reports', 'market-intelligence', 'screening', 'underwriting', 'committee', 'intake', 'closing', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Net Yield', value: '9.84%', change: '+18 bps', positive: true },
        { label: 'Refi Wall', value: '$612M', change: '+$74M', positive: false },
        { label: 'Risk Budget', value: '71% Used', change: '-4%', positive: true },
      ],
      signals: [
        { label: 'Carry Protection', value: 'PRIORITY', tone: 'text-amber-300' },
        { label: 'Extension Risk', value: 'ELEVATED', tone: 'text-red-400' },
        { label: 'Servicing Drift', value: 'MONITORED', tone: 'text-stone-300' },
      ],
    },
    'risk-manager': {
      deskLabel: 'Enterprise Risk Grid',
      objective: 'Contain downside through covenant control, concentration limits, adverse-scenario readiness, and policy discipline.',
      summary: 'Put trigger breaches, downgrade vectors, collateral deterioration, and reporting obligations in the first visual plane.',
      dashboardHeadline: 'Risk-governed market operations with trigger governance, adverse scenarios, and concentration control moved to the front of the terminal.',
      dashboardSummary: 'This mode reorganizes the platform around what can break first: watchlists, triggers, ratings pressure, and policy exceptions.',
      dashboardNotes: [
        'The surface leads with breach management and concentration heat instead of production totals.',
        'Cross-asset deterioration can be spotted as one coordinated risk book rather than siloed desk noise.',
        'Automated reporting and event packs are promoted because unresolved exceptions are as dangerous as unresolved positions.',
      ],
      moduleOrder: ['asset-management', 'reports', 'market-intelligence', 'screening', 'committee', 'underwriting', 'command-center', 'intake', 'closing', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Open Exceptions', value: '11', change: '-2', positive: true },
        { label: 'Downgrade Exposure', value: '$284M', change: '+$36M', positive: false },
        { label: 'Policy SLA', value: '97.2%', change: '+1.3%', positive: true },
      ],
      signals: [
        { label: 'Trigger Queue', value: 'ACTIVE', tone: 'text-red-400' },
        { label: 'Control Coverage', value: 'GREEN', tone: 'text-emerald-400' },
        { label: 'Scenario Pack', value: 'UPDATED', tone: 'text-amber-300' },
      ],
    },
  },
  cre: {
    originator: {
      deskLabel: 'CRE Origination',
      objective: 'Push sponsor coverage, screening, quoting, and commitment cadence without losing pricing discipline.',
      summary: 'Promote the live pipeline, stage velocity, sponsor touchpoints, and fallout risk across the CRE workflow.',
      dashboardHeadline: 'A CRE deal desk for sourcing, sizing, quoting, and funding loans with sponsor intelligence and pricing velocity at the front.',
      dashboardSummary: 'The workbench prioritizes new deal movement, stage conversion, and sponsor dialogue. Surveillance still remains visible, but it no longer crowds out origination throughput.',
      dashboardNotes: [
        'Pipeline heat is promoted over static portfolio summaries so the team can attack execution bottlenecks immediately.',
        'Conversion timing and model overlays sit right behind the origination desk because pricing and structure drive win rate.',
        'Reporting stays in view for committee speed, not as an afterthought at the end of the process.',
      ],
      moduleOrder: ['intake', 'closing', 'market-intelligence', 'reports', 'command-center', 'asset-management', 'screening', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Quoted Pipeline', value: '$1.48B', change: '+9.2%', positive: true },
        { label: 'Sponsor Meetings', value: '17', change: '+4', positive: true },
        { label: 'Fallout Risk', value: '$126M', change: '+$28M', positive: false },
      ],
      signals: [
        { label: 'Deal Intake', value: 'ACTIVE', tone: 'text-emerald-400' },
        { label: 'Committee Queue', value: '9 LIVE', tone: 'text-amber-300' },
        { label: 'Rate Locks', value: '4 TODAY', tone: 'text-stone-300' },
      ],
    },
    'asset-manager': {
      deskLabel: 'CRE Asset Oversight',
      objective: 'Protect NOI, occupancy, borrower compliance, and extension outcomes across the financed book.',
      summary: 'Bring watchlists, covenant drift, servicing actions, and refinance readiness into the center of the screen.',
      dashboardHeadline: 'CRE asset management with covenant pressure, NOI drift, maturity walls, and intervention workflows elevated above raw originations.',
      dashboardSummary: 'This mode pivots the same data set toward portfolio preservation: asset quality, cash flow durability, and servicing action cadence.',
      dashboardNotes: [
        'Long-duration value loss usually starts as small operational drift, so the workbench puts deterioration metrics first.',
        'Origination remains accessible, but asset surveillance and reporting are treated as the primary operating loop.',
        'Watchlist actions and triggered reporting stay close to portfolio metrics so nothing material falls between teams.',
      ],
      moduleOrder: ['asset-management', 'intake', 'reports', 'command-center', 'market-intelligence', 'closing', 'screening', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'At-Risk NOI', value: '$23.4M', change: '+$3.1M', positive: false },
        { label: 'Watchlist Loans', value: '14', change: '-1', positive: true },
        { label: 'Occupancy Drift', value: '-110 bps', change: '+25 bps', positive: false },
      ],
      signals: [
        { label: 'Servicing Actions', value: 'OPEN', tone: 'text-amber-300' },
        { label: 'Portfolio Health', value: 'MIXED', tone: 'text-stone-300' },
        { label: 'Covenant Pressure', value: 'ELEVATED', tone: 'text-red-400' },
      ],
    },
    'portfolio-manager': {
      deskLabel: 'CRE Portfolio',
      objective: 'Manage exposure, yield durability, refinancing risk, and hold-versus-distribute decisions across CRE.',
      summary: 'Position the book around carry, concentration, and exit readiness while keeping asset and trading pathways connected.',
      dashboardHeadline: 'CRE portfolio management with yield durability, refinancing pressure, and hold-versus-distribute decisions in the main frame.',
      dashboardSummary: 'The same loans are now arranged for long-horizon allocation decisions. Carry protection, surveillance, and exit optionality lead the experience.',
      dashboardNotes: [
        'This lens suppresses vanity pipeline noise and emphasizes the variables that reshape realized yield over time.',
        'Distribution and securitization options remain visible because they directly influence balance-sheet duration and liquidity.',
        'Reporting moves higher because committee and investor alignment are part of capital management, not just administration.',
      ],
      moduleOrder: ['asset-management', 'command-center', 'reports', 'intake', 'screening', 'market-intelligence', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Net Carry', value: '8.94%', change: '+14 bps', positive: true },
        { label: 'Maturing Next 12M', value: '$438M', change: '+$52M', positive: false },
        { label: 'Distribute Candidates', value: '7', change: '+2', positive: true },
      ],
      signals: [
        { label: 'Hold Book', value: 'STABLE', tone: 'text-emerald-400' },
        { label: 'Exit Window', value: 'SELECTIVE', tone: 'text-amber-300' },
        { label: 'Yield Drift', value: 'WATCH', tone: 'text-stone-300' },
      ],
    },
    'secondary-trader': {
      deskLabel: 'CRE Relative Value',
      objective: 'Exploit price dislocation in whole loans and CRE credit while tracking holder history and exit paths.',
      summary: 'Bias the platform toward inventory, marks, historical prints, dealer color, and buyer coverage instead of underwriting cadence.',
      dashboardHeadline: 'A CRE credit trading lens where price action, whole-loan inventory, position history, and exit timing outrank production workflow.',
      dashboardSummary: 'This configuration keeps the same assets visible, but the desk now behaves like a trading terminal. Price and flow lead; process trails.',
      dashboardNotes: [
        'Whole-loan and securitized exit paths rise to the top because the desk is optimizing liquidity and basis, not committee throughput.',
        'Historical marks, holders, and realized sale levels are elevated over sponsor notes and credit memo polish.',
        'Surveillance remains close because deterioration changes bid depth long before it changes marketing copy.',
      ],
      moduleOrder: ['screening', 'intake', 'asset-management', 'reports', 'command-center', 'market-intelligence', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Loan Inventory', value: '$286M', change: '+$48M', positive: true },
        { label: 'Bid Wanted', value: '19 Lines', change: '+6', positive: true },
        { label: 'Mark Slippage', value: '-32 bps', change: '-9 bps', positive: false },
      ],
      signals: [
        { label: 'Dealer Color', value: 'FIRM', tone: 'text-emerald-400' },
        { label: 'Seller Flow', value: 'HEAVY', tone: 'text-amber-300' },
        { label: 'Basis Risk', value: 'LIVE', tone: 'text-red-400' },
      ],
    },
    'risk-manager': {
      deskLabel: 'CRE Risk Control',
      objective: 'Control downside through covenant enforcement, sponsor weakness monitoring, and collateral deterioration triage.',
      summary: 'Push maturing loans, occupancy breaks, DSCR stress, and documentation exceptions to the front of the workspace.',
      dashboardHeadline: 'CRE risk control with maturity walls, covenant breaches, collateral stress, and unresolved exceptions driving the layout.',
      dashboardSummary: 'Instead of showing how many deals are active, the platform now shows where capital is exposed to downside and where intervention is required first.',
      dashboardNotes: [
        'Risk discipline in CRE is about speed and visibility, so watchlists and triggers take precedence over intake and marketing.',
        'Model overlays matter here because subtle assumption changes often precede covenant breaks.',
        'Triggered reporting remains prominent so internal stakeholders and counterparties receive the right signal at the right time.',
      ],
      moduleOrder: ['asset-management', 'reports', 'market-intelligence', 'intake', 'command-center', 'closing', 'screening', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Overdue Triggers', value: '9', change: '-1', positive: true },
        { label: 'Maturity Stress', value: '$212M', change: '+$37M', positive: false },
        { label: 'Exception SLA', value: '95.8%', change: '+0.9%', positive: true },
      ],
      signals: [
        { label: 'Watchlist', value: 'ACTIVE', tone: 'text-red-400' },
        { label: 'Reporting Queue', value: 'READY', tone: 'text-amber-300' },
        { label: 'Policy Drift', value: 'LOW', tone: 'text-emerald-400' },
      ],
    },
  },
  cmbs: {
    'secondary-trader': {
      deskLabel: 'CMBS Trading',
      objective: 'Trade dislocated bonds and pools using spread moves, holder maps, BWIC color, and historical run data.',
      summary: 'Surface price action, liquidity, basis, and position history before primary structuring details.',
      dashboardHeadline: 'A CMBS trading terminal with spread movement, BWIC flow, holder intelligence, and historical prints pushed ahead of pool engineering.',
      dashboardSummary: 'The desk now reads like a bond market surface. Secondary liquidity and price discovery lead, while structuring remains one click away.',
      dashboardNotes: [
        'Price action, dealer axes, and inventory age now take precedence over issuance sequencing because trade timing is the main edge.',
        'Asset surveillance remains close since collateral deterioration affects liquidity and mark stability quickly.',
        'Reporting stays high in the stack so trader-to-PM communication and realized PnL context are always adjacent to the book.',
      ],
      moduleOrder: ['screening', 'reports', 'asset-management', 'command-center', 'market-intelligence', 'intake', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'AAA Spread', value: '+85', change: '-3 bps', positive: true },
        { label: 'BWIC Notional', value: '$164M', change: '+$52M', positive: true },
        { label: 'Inventory PnL', value: '+$2.8M', change: '+$0.6M', positive: true },
      ],
      signals: [
        { label: 'Street Color', value: 'ACTIVE', tone: 'text-emerald-400' },
        { label: 'Dealer Axes', value: '7 DESKS', tone: 'text-amber-300' },
        { label: 'Volatility', value: 'ELEVATED', tone: 'text-red-400' },
      ],
    },
    structurer: {
      deskLabel: 'CMBS Structuring',
      objective: 'Optimize pool composition, subordination, and tranche economics while keeping ratings and distribution aligned.',
      summary: 'The workbench elevates pool cuts, tranche stack, investor feedback, and timeline execution over secondary marks.',
      dashboardHeadline: 'A CMBS structuring bench centered on pool engineering, ratings readiness, waterfall design, and distribution sequencing.',
      dashboardSummary: 'This view privileges the build process: pool integrity, capital stack math, and execution readiness now determine the shell.',
      dashboardNotes: [
        'Pool composition and timeline milestones sit above generic enterprise KPIs because execution quality is a structural problem first.',
        'Model overlays stay nearby to pressure-test assumptions before they become ratings or investor objections.',
        'Reporting is near the top because roadshow, memo, and tape output must move at structuring speed.',
      ],
      moduleOrder: ['screening', 'market-intelligence', 'reports', 'command-center', 'intake', 'closing', 'asset-management', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Pools In Build', value: '3', change: '+1', positive: true },
        { label: 'AAA Credit Support', value: '29.8%', change: '+20 bps', positive: true },
        { label: 'Execution Slippage', value: '4 Days', change: '+1.0d', positive: false },
      ],
      signals: [
        { label: 'Pool Cut', value: 'ON TRACK', tone: 'text-emerald-400' },
        { label: 'Ratings Loop', value: 'ACTIVE', tone: 'text-amber-300' },
        { label: 'Tape Quality', value: 'CONTROLLED', tone: 'text-stone-300' },
      ],
    },
    'risk-manager': {
      deskLabel: 'CMBS Risk',
      objective: 'Track downgrade pressure, collateral deterioration, maturity cliffs, and tail-risk exposure across bonds and pools.',
      summary: 'Move surveillance, ratings sensitivity, and event packs ahead of issuance momentum or trader enthusiasm.',
      dashboardHeadline: 'CMBS risk surveillance with downgrade vectors, refinance pressure, and bond-tail exposure driving the workspace.',
      dashboardSummary: 'This lens is built for what can go wrong in structured CRE credit: spread gaps, collateral cracks, and ratings migration.',
      dashboardNotes: [
        'The platform prioritizes adverse movement in the underlying collateral and capital stack before highlighting realized gains.',
        'Triggered reporting and model overlays are promoted because bad assumptions become expensive faster than bad layouts do.',
        'Structuring remains close because subordination decisions are one of the few immediate levers for risk reduction.',
      ],
      moduleOrder: ['asset-management', 'screening', 'reports', 'market-intelligence', 'command-center', 'intake', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Downgrade Watch', value: '$118M', change: '+$21M', positive: false },
        { label: 'Refi Exposure', value: '$403M', change: '+$44M', positive: false },
        { label: 'Trigger Resolution', value: '96.1%', change: '+1.7%', positive: true },
      ],
      signals: [
        { label: 'Ratings Drift', value: 'MONITORED', tone: 'text-amber-300' },
        { label: 'Bond Tail', value: 'OPEN', tone: 'text-red-400' },
        { label: 'Exception Pack', value: 'READY', tone: 'text-emerald-400' },
      ],
    },
    'portfolio-manager': {
      deskLabel: 'CMBS PM View',
      objective: 'Manage risk-adjusted carry, liquidity, and allocation across primary and secondary CMBS positions.',
      summary: 'Balance spread capture against collateral stability, duration, and exit depth.',
      dashboardHeadline: 'A CMBS portfolio lens where carry, liquidity, and collateral durability are considered together instead of as separate workflows.',
      dashboardSummary: 'This mode merges structuring intelligence and secondary execution into one allocation framework so the PM can decide hold, add, or distribute faster.',
      dashboardNotes: [
        'Spread opportunity only matters if the book can carry it; this layout keeps surveillance and reporting adjacent to pricing.',
        'Structuring remains high because primary execution can be a portfolio management tool, not just a production function.',
        'Model comparisons stay nearby to make allocation changes defendable to IC and investors.',
      ],
      moduleOrder: ['command-center', 'screening', 'asset-management', 'reports', 'market-intelligence', 'intake', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Carry', value: '11.2%', change: '+24 bps', positive: true },
        { label: 'Liquidity Score', value: '7.6 / 10', change: '-0.3', positive: false },
        { label: 'Spread Cushion', value: '42 bps', change: '+8 bps', positive: true },
      ],
      signals: [
        { label: 'Allocation', value: 'ACTIVE', tone: 'text-emerald-400' },
        { label: 'Exit Optionality', value: 'GOOD', tone: 'text-amber-300' },
        { label: 'Collateral Drift', value: 'WATCH', tone: 'text-stone-300' },
      ],
    },
  },
  rmbs: {
    'secondary-trader': {
      deskLabel: 'RMBS Trading',
      objective: 'Trade prepayment, delinquency, and relative-value dislocations across RMBS collateral and tranches.',
      summary: 'Prioritize tape behavior, carry versus convexity, and position history over origination-style loan detail.',
      dashboardHeadline: 'An RMBS trading layout centered on prepay behavior, delinquency migration, spread dislocation, and dealer flow.',
      dashboardSummary: 'The desk now behaves like a mortgage bond market screen. The emphasis is on tradeable behavior, not just collateral description.',
      dashboardNotes: [
        'Prepay and delinquency behavior move to the top because they directly control marks and carry in RMBS.',
        'Model overlays matter here because small assumption changes create visible relative-value moves.',
        'Reports remain accessible for PM and investor communication, but they no longer dominate the main frame.',
      ],
      moduleOrder: ['underwriting', 'reports', 'market-intelligence', 'command-center', 'asset-management', 'closing', 'screening', 'committee', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Current Coupon OAS', value: '+118', change: '-6 bps', positive: true },
        { label: 'Street Flow', value: '$208M', change: '+$63M', positive: true },
        { label: 'CPR Drift', value: '+0.7', change: '+0.2', positive: false },
      ],
      signals: [
        { label: 'Dealer Color', value: 'HEAVY', tone: 'text-amber-300' },
        { label: 'Relative Value', value: 'OPEN', tone: 'text-emerald-400' },
        { label: 'Convexity Risk', value: 'LIVE', tone: 'text-red-400' },
      ],
    },
    structurer: {
      deskLabel: 'RMBS Structuring',
      objective: 'Engineer pools and tranche mix around borrower tape quality, prepay expectations, and investor appetite.',
      summary: 'Put credit box shape, collateral stratification, and issuance mechanics ahead of secondary flow.',
      dashboardHeadline: 'RMBS structuring with pool stratification, expected prepay path, and investor distribution sequence leading the terminal.',
      dashboardSummary: 'This lens turns the RMBS workflow into a build bench. The focus is on how the collateral becomes a durable, marketable structure.',
      dashboardNotes: [
        'The shell elevates collateral mix, tranche math, and tape readiness because that is what controls pricing quality.',
        'Model intelligence remains adjacent to expose assumption divergence before issuance.',
        'Reporting moves high because tapes, memos, and investor output are inseparable from structure quality.',
      ],
      moduleOrder: ['underwriting', 'market-intelligence', 'reports', 'command-center', 'asset-management', 'closing', 'screening', 'committee', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Pools In Prep', value: '2', change: '+1', positive: true },
        { label: 'Projected CPR', value: '7.5%', change: '-0.4%', positive: true },
        { label: 'Execution Window', value: '11 Days', change: '-2d', positive: true },
      ],
      signals: [
        { label: 'Tape Integrity', value: 'GREEN', tone: 'text-emerald-400' },
        { label: 'Investor Feedback', value: 'ACTIVE', tone: 'text-amber-300' },
        { label: 'Model Drift', value: 'LOW', tone: 'text-stone-300' },
      ],
    },
    'risk-manager': {
      deskLabel: 'RMBS Risk',
      objective: 'Monitor delinquency migration, extension risk, trigger thresholds, and pool deterioration.',
      summary: 'Bring collateral breakpoints, assumption stress, and triggered communications into primary view.',
      dashboardHeadline: 'RMBS risk control with delinquency migration, extension behavior, and collateral breakpoints driving the screen.',
      dashboardSummary: 'The system reorganizes around stress points in the borrower tape and tranche stack rather than around top-line issuance flow.',
      dashboardNotes: [
        'This lens promotes the variables most likely to impair marks or distributions: delinquency, default, and extension.',
        'Reporting is close because escalation speed matters as much as diagnosis quality in RMBS.',
        'Model overlays are elevated because scenario sensitivity is central to risk control, not just analytics polish.',
      ],
      moduleOrder: ['asset-management', 'underwriting', 'reports', 'market-intelligence', 'command-center', 'closing', 'screening', 'committee', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: '60+ DQ Drift', value: '+22 bps', change: '+7 bps', positive: false },
        { label: 'Loss Trigger Buffer', value: '1.8x', change: '-0.2x', positive: false },
        { label: 'Escalation SLA', value: '98.4%', change: '+0.6%', positive: true },
      ],
      signals: [
        { label: 'Tape Stress', value: 'MONITORED', tone: 'text-red-400' },
        { label: 'Scenario Pack', value: 'READY', tone: 'text-amber-300' },
        { label: 'Escalation', value: 'CONTROLLED', tone: 'text-emerald-400' },
      ],
    },
    'portfolio-manager': {
      deskLabel: 'RMBS PM View',
      objective: 'Balance carry, convexity, and credit performance across RMBS holdings and new allocations.',
      summary: 'Keep duration and borrower behavior tied to realized PnL and portfolio capacity decisions.',
      dashboardHeadline: 'RMBS portfolio management with carry, convexity, and credit behavior integrated into one allocation cockpit.',
      dashboardSummary: 'This view is tuned for hold-size, rotate, and distribute decisions. The desk leads with performance durability, not just live flow.',
      dashboardNotes: [
        'Prepay and credit behavior are adjacent to reporting because portfolio decisions require both market and communication discipline.',
        'Trading remains high in the module stack, but surveillance is not separated from it because collateral behavior shapes returns.',
        'Model intelligence helps translate borrower-level assumptions into portfolio-level conviction.',
      ],
      moduleOrder: ['command-center', 'underwriting', 'asset-management', 'reports', 'market-intelligence', 'closing', 'screening', 'committee', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Portfolio Carry', value: '10.6%', change: '+11 bps', positive: true },
        { label: 'Duration Gap', value: '0.42y', change: '+0.08y', positive: false },
        { label: 'Spread Cushion', value: '36 bps', change: '+5 bps', positive: true },
      ],
      signals: [
        { label: 'Allocation', value: 'ACTIVE', tone: 'text-emerald-400' },
        { label: 'Duration Risk', value: 'WATCH', tone: 'text-amber-300' },
        { label: 'Credit Drift', value: 'LOW', tone: 'text-stone-300' },
      ],
    },
  },
  clo: {
    'secondary-trader': {
      deskLabel: 'CLO Trading',
      objective: 'Trade CLO tranches around spread dislocation, manager quality, and test headroom.',
      summary: 'Promote price action, manager inventory, and tranche-level relative value over new issue mechanics.',
      dashboardHeadline: 'A CLO trading view tuned for tranche spread movement, manager color, and test headroom instead of warehouse administration.',
      dashboardSummary: 'This lens turns the CLO module into a relative-value screen with enough surveillance context to avoid buying hidden deterioration.',
      dashboardNotes: [
        'Spread opportunity and manager quality sit first because execution speed matters more than build sequencing in this mode.',
        'Surveillance stays adjacent because WARF and OC/IC drift can trap value quickly.',
        'Reporting remains accessible for PM and investor alignment, but not at the expense of trade timing.',
      ],
      moduleOrder: ['committee', 'reports', 'asset-management', 'command-center', 'market-intelligence', 'closing', 'screening', 'underwriting', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'AAA Spread', value: '+145', change: '-4 bps', positive: true },
        { label: 'Street Inventory', value: '$182M', change: '+$39M', positive: true },
        { label: 'Test Cushion', value: '2.6 pts', change: '-0.4 pts', positive: false },
      ],
      signals: [
        { label: 'Manager Color', value: 'ACTIVE', tone: 'text-emerald-400' },
        { label: 'BWIC Flow', value: 'OPEN', tone: 'text-amber-300' },
        { label: 'Test Pressure', value: 'LIVE', tone: 'text-red-400' },
      ],
    },
    structurer: {
      deskLabel: 'CLO Structuring',
      objective: 'Optimize warehouses, tranche economics, and test cushions while coordinating manager and investor requirements.',
      summary: 'Lead with collateral quality, OC/IC mathematics, and issuance readiness rather than live secondary marks.',
      dashboardHeadline: 'CLO structuring with warehouse build, tranche economics, and test optimization elevated to the top of the platform.',
      dashboardSummary: 'This mode is for engineering the deal correctly before the market sees it: collateral quality, structure resilience, and execution timing lead.',
      dashboardNotes: [
        'Warehouse composition and tranche test cushion come before market noise because structure quality is the main control lever.',
        'Model intelligence stays high to compare collateral and tranche assumptions before finalizing the stack.',
        'Reporting remains prominent because trustee, investor, and committee outputs must move at structuring speed.',
      ],
      moduleOrder: ['committee', 'market-intelligence', 'reports', 'command-center', 'asset-management', 'closing', 'screening', 'underwriting', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Warehouse Ramp', value: '78%', change: '+6%', positive: true },
        { label: 'AAA Cushion', value: '132.5%', change: '-0.3 pts', positive: false },
        { label: 'Launch Window', value: '14 Days', change: '-3d', positive: true },
      ],
      signals: [
        { label: 'Warehouse', value: 'BUILDING', tone: 'text-amber-300' },
        { label: 'OC/IC Tests', value: 'MONITORED', tone: 'text-emerald-400' },
        { label: 'Collateral Mix', value: 'CONTROLLED', tone: 'text-stone-300' },
      ],
    },
    'risk-manager': {
      deskLabel: 'CLO Risk',
      objective: 'Watch WARF drift, industry concentration, OC/IC tests, and tranche tail exposure before breaches happen.',
      summary: 'Move structural breakpoints and deteriorating collateral quality ahead of new issue progress or secondary excitement.',
      dashboardHeadline: 'CLO risk governance with OC/IC tests, WARF drift, and manager concentration pressure driving the operating view.',
      dashboardSummary: 'This layout focuses on the failure modes of leveraged credit structures. Test erosion and collateral quality lead the platform.',
      dashboardNotes: [
        'The main frame surfaces test cushion and collateral degradation before showing spread gains or launch timing.',
        'Reports and model overlays are promoted because escalation and scenario discipline matter directly to downside control.',
        'Asset surveillance remains tied to the CLO desk so deteriorating collateral cannot hide inside structure-level metrics.',
      ],
      moduleOrder: ['asset-management', 'committee', 'reports', 'market-intelligence', 'command-center', 'closing', 'screening', 'underwriting', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'OC Test Headroom', value: '2.4 pts', change: '-0.5 pts', positive: false },
        { label: 'WARF Drift', value: '+45', change: '+18', positive: false },
        { label: 'Exception SLA', value: '97.6%', change: '+1.1%', positive: true },
      ],
      signals: [
        { label: 'Tests', value: 'ACTIVE', tone: 'text-red-400' },
        { label: 'Manager Risk', value: 'WATCH', tone: 'text-amber-300' },
        { label: 'Escalation Pack', value: 'READY', tone: 'text-emerald-400' },
      ],
    },
    'portfolio-manager': {
      deskLabel: 'CLO PM View',
      objective: 'Allocate to CLO risk with an eye on spread carry, manager quality, and structural resilience.',
      summary: 'Keep realized carry, secondary liquidity, and structural test health in one integrated view.',
      dashboardHeadline: 'CLO portfolio management with carry, manager selection, and test resilience integrated into one premium workbench.',
      dashboardSummary: 'The PM view merges trading, surveillance, and reporting so allocation decisions can reflect both return and structural durability.',
      dashboardNotes: [
        'Carry opportunity only matters if the structure can keep it, so test headroom is shown alongside spread and liquidity.',
        'Reports and model overlays are lifted because portfolio decisions must remain explainable to investors and IC.',
        'Secondary flow remains visible because CLO PM work often alternates between holding, rotating, and trimming risk.',
      ],
      moduleOrder: ['command-center', 'committee', 'asset-management', 'reports', 'market-intelligence', 'closing', 'screening', 'underwriting', 'intake', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Net Carry', value: '13.1%', change: '+19 bps', positive: true },
        { label: 'Liquidity Score', value: '6.9 / 10', change: '-0.2', positive: false },
        { label: 'Manager Concentration', value: '24%', change: '+2%', positive: false },
      ],
      signals: [
        { label: 'Allocation', value: 'ACTIVE', tone: 'text-emerald-400' },
        { label: 'Manager Quality', value: 'SELECTIVE', tone: 'text-amber-300' },
        { label: 'Test Cushion', value: 'WATCH', tone: 'text-stone-300' },
      ],
    },
  },
  'whole-loans': {
    originator: {
      deskLabel: 'Whole Loan Production',
      objective: 'Source, size, and close whole loans with fast tape triage and disciplined bid/quote control.',
      summary: 'Promote tape intake, bid discipline, sponsor connectivity, and exit path readiness for whole-loan production.',
      dashboardHeadline: 'A whole-loan desk for sourcing, underwriting, bidding, and exiting inventory with premium speed and market awareness.',
      dashboardSummary: 'This mode treats whole loans as a production and distribution business. Bid discipline, tape quality, and buyer readiness come first.',
      dashboardNotes: [
        'The workbench foregrounds intake, selection, and conversion because whole-loan production is a velocity business.',
        'Reporting stays close because tape packs and committee books must be generated at the same speed as bids.',
        'Asset surveillance is still visible because whole-loan inventory can turn into carry risk if it sits too long.',
      ],
      moduleOrder: ['intake', 'closing', 'reports', 'command-center', 'market-intelligence', 'asset-management', 'screening', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Tape Volume', value: '$612M', change: '+$88M', positive: true },
        { label: 'Bid Hit Rate', value: '28%', change: '+3 pts', positive: true },
        { label: 'Aged Inventory', value: '$74M', change: '+$12M', positive: false },
      ],
      signals: [
        { label: 'Tape Flow', value: 'HEAVY', tone: 'text-emerald-400' },
        { label: 'Bid Desk', value: 'ACTIVE', tone: 'text-amber-300' },
        { label: 'Warehouse Use', value: 'ELEVATED', tone: 'text-red-400' },
      ],
    },
    'secondary-trader': {
      deskLabel: 'Whole Loan Trading',
      objective: 'Trade whole-loan inventory using historical marks, buyer maps, and basis between whole and securitized exits.',
      summary: 'Bias the workspace toward price history, current marks, inventory age, and buyer coverage.',
      dashboardHeadline: 'A whole-loan trading surface with buyer depth, historical prints, and basis-aware exit paths at the center of the platform.',
      dashboardSummary: 'This configuration shifts the platform from underwriting posture to market posture. Marks, buyers, and exit timing now lead the terminal.',
      dashboardNotes: [
        'Holder history and realized sale levels matter more here than pipeline stage labels, so the module order reflects that.',
        'CMBS and reporting remain near the top because securitized exits and communication are part of trade execution.',
        'Surveillance stays visible because deterioration changes executable price before it changes stated credit opinion.',
      ],
      moduleOrder: ['intake', 'screening', 'reports', 'command-center', 'asset-management', 'market-intelligence', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Inventory Marks', value: '+18 bps', change: '+7 bps', positive: true },
        { label: 'Buyer Coverage', value: '23 Accounts', change: '+5', positive: true },
        { label: 'Basis Move', value: '-14 bps', change: '-4 bps', positive: false },
      ],
      signals: [
        { label: 'Buyer Depth', value: 'GOOD', tone: 'text-emerald-400' },
        { label: 'Dealer Color', value: 'ACTIVE', tone: 'text-amber-300' },
        { label: 'Inventory Age', value: 'WATCH', tone: 'text-red-400' },
      ],
    },
    'asset-manager': {
      deskLabel: 'Whole Loan Asset Oversight',
      objective: 'Protect performance on retained whole loans through active servicing, covenant awareness, and borrower intervention.',
      summary: 'Put retained inventory health and action queues ahead of new tape flow.',
      dashboardHeadline: 'Whole-loan asset management with borrower performance, carry quality, and servicing action queues leading the workbench.',
      dashboardSummary: 'Retained whole-loan positions need a different posture from active production. This view turns the platform into a carry-defense surface.',
      dashboardNotes: [
        'The same loans are now framed as retained assets, not just inventory waiting to be sold.',
        'Servicing, triggers, and reports rise to the top because intervention speed controls realized outcome.',
        'Origination stays close for context, but surveillance determines the sequence of work.',
      ],
      moduleOrder: ['asset-management', 'intake', 'reports', 'command-center', 'market-intelligence', 'closing', 'screening', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Retained UPB', value: '$348M', change: '+$24M', positive: false },
        { label: 'Delinquent Balance', value: '$29M', change: '-$6M', positive: true },
        { label: 'Servicing SLA', value: '96.8%', change: '+1.2%', positive: true },
      ],
      signals: [
        { label: 'Carry Book', value: 'ACTIVE', tone: 'text-amber-300' },
        { label: 'Interventions', value: 'OPEN', tone: 'text-red-400' },
        { label: 'Yield Protection', value: 'ON', tone: 'text-emerald-400' },
      ],
    },
    'portfolio-manager': {
      deskLabel: 'Whole Loan PM View',
      objective: 'Decide what to hold, finance, distribute, or securitize while preserving net yield and liquidity flexibility.',
      summary: 'Balance warehouse usage, carry, credit performance, and exit route optionality across the whole-loan book.',
      dashboardHeadline: 'Whole-loan portfolio management with warehouse efficiency, carry durability, and exit optionality driving the layout.',
      dashboardSummary: 'This mode links origination, surveillance, and securitized exit choices into one portfolio decision framework.',
      dashboardNotes: [
        'Whole-loan capital is only attractive when it can be financed and exited efficiently, so warehouse and exit views are kept close.',
        'Reports and surveillance are promoted because portfolio decisions require durable performance context, not just pipeline velocity.',
        'Model intelligence remains nearby to test whether the carry being shown is robust under different assumptions.',
      ],
      moduleOrder: ['command-center', 'asset-management', 'intake', 'reports', 'screening', 'market-intelligence', 'closing', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Net Carry', value: '10.1%', change: '+9 bps', positive: true },
        { label: 'Warehouse Utilization', value: '82%', change: '+6%', positive: false },
        { label: 'Exit Candidates', value: '11', change: '+3', positive: true },
      ],
      signals: [
        { label: 'Financing', value: 'TIGHT', tone: 'text-amber-300' },
        { label: 'Exit Optionality', value: 'GOOD', tone: 'text-emerald-400' },
        { label: 'Yield Drift', value: 'WATCH', tone: 'text-stone-300' },
      ],
    },
    'risk-manager': {
      deskLabel: 'Whole Loan Risk',
      objective: 'Contain warehouse and retained-loan downside through aging, covenant, and collateral deterioration controls.',
      summary: 'Move aged inventory, delinquency, and concentration stress ahead of production vanity metrics.',
      dashboardHeadline: 'Whole-loan risk governance with aging inventory, delinquency, warehouse strain, and exception control leading the terminal.',
      dashboardSummary: 'This lens focuses the platform on retained risk and operational breakpoints rather than bid volume alone.',
      dashboardNotes: [
        'Whole-loan desks can accumulate risk quietly, so aging and warehouse pressure are promoted into the first visual layer.',
        'Reporting sits high because escalation is part of downside control, not just back-office processing.',
        'Asset surveillance and origination remain adjacent so risk can separate good flow from bad inventory quickly.',
      ],
      moduleOrder: ['asset-management', 'reports', 'intake', 'market-intelligence', 'command-center', 'closing', 'screening', 'underwriting', 'committee', 'pipeline', 'surveillance', 'valuation'],
      snapshots: [
        { label: 'Aged Inventory', value: '$97M', change: '+$18M', positive: false },
        { label: '30+ DQ', value: '$42M', change: '+$5M', positive: false },
        { label: 'Exception SLA', value: '97.1%', change: '+0.8%', positive: true },
      ],
      signals: [
        { label: 'Inventory Risk', value: 'HIGH', tone: 'text-red-400' },
        { label: 'Escalation', value: 'READY', tone: 'text-amber-300' },
        { label: 'Control Stack', value: 'GREEN', tone: 'text-emerald-400' },
      ],
    },
  },
}

function pickPreset(asset: FocusAssetId, persona: PersonaId): ProfilePreset {
  const assetProfiles = PROFILE_PRESETS[asset]
  const profile = assetProfiles[persona]
  if (profile) return profile
  const fallbackPersona = PERSONAS_BY_ASSET[asset][0]
  return assetProfiles[fallbackPersona] ?? PROFILE_PRESETS.enterprise.executive!
}

function pulse(label: string, value: string, tone: string) {
  return { label, value, tone }
}

function buildDashboardLens(asset: FocusAssetId, persona: PersonaId, preset: ProfilePreset): LensModule {
  return {
    summary: preset.summary,
    monitors: [
      `Top desk order: ${preset.moduleOrder.slice(0, 3).map((moduleId) => MODULE_META[moduleId].label).join(' / ')}`,
      `${ASSET_META[asset].label} mandate: ${ASSET_META[asset].description}`,
      `${PERSONA_META[persona].label} objective: ${preset.objective}`,
    ],
    automations: [
      'Re-rank the shell and quick actions when the desk lens changes.',
      'Keep reporting, surveillance, and model support bound to the same operating context.',
      'Preserve the current module while reprioritizing the surrounding workspace instantly.',
    ],
    pulse: [
      pulse(preset.snapshots[0].label, preset.snapshots[0].value, preset.snapshots[0].positive ? 'text-emerald-400' : 'text-red-400'),
      pulse(preset.snapshots[1].label, preset.snapshots[1].value, preset.snapshots[1].positive ? 'text-emerald-400' : 'text-red-400'),
      pulse('Desk Mode', preset.deskLabel, 'text-amber-300'),
    ],
  }
}

function buildCreLens(asset: FocusAssetId, persona: PersonaId): LensModule {
  if (persona === 'originator') {
    return {
      summary: 'Center the CRE desk on sponsor touchpoints, active quotes, stage velocity, and which files are stalling before commitment.',
      monitors: ['Quoted pipeline by size and sponsor', 'Stage conversion slippage and deal fallout', 'Committee memo readiness and rate-lock windows'],
      automations: ['Escalate stalled files after stage SLA breaches', 'Refresh sponsor and committee packs from the live deal record', 'Flag deals where pricing no longer clears mandate or market'],
      pulse: [pulse('Priority Lane', 'Quotes + Commitments', 'text-amber-300'), pulse('Execution Bias', 'Velocity', 'text-emerald-400'), pulse('Coverage', 'Sponsor First', 'text-stone-300')],
    }
  }
  if (persona === 'secondary-trader') {
    return {
      summary: 'Use the CRE desk as a whole-loan inventory blotter: marks, historical sale levels, buyers, and executable exit routes matter most.',
      monitors: ['Current inventory and aging by borrower', 'Historical prints and buyer maps', 'Basis between whole-loan exits and securitized outcomes'],
      automations: ['Push stale inventory into exit review queues', 'Attach last sale levels and counterparties to each position', 'Trigger mark review when collateral or spread moves break thresholds'],
      pulse: [pulse('Priority Lane', 'Inventory + Marks', 'text-amber-300'), pulse('Execution Bias', 'Liquidity', 'text-emerald-400'), pulse('Coverage', 'Buyer Depth', 'text-stone-300')],
    }
  }
  if (persona === 'risk-manager') {
    return {
      summary: 'Turn the CRE module into a risk triage surface with covenant breaks, sponsor weakness, maturity walls, and document exceptions up front.',
      monitors: ['DSCR and LTV breach clusters', 'Maturities and extension exposure', 'Incomplete diligence or covenant artifacts'],
      automations: ['Escalate material covenant breaks to watchlist and reporting', 'Open exception tasks on missing diligence packages', 'Route maturity stress to refinance action queues'],
      pulse: [pulse('Priority Lane', 'Exceptions', 'text-red-400'), pulse('Execution Bias', 'Containment', 'text-amber-300'), pulse('Coverage', 'Policy', 'text-stone-300')],
    }
  }
  if (persona === 'asset-manager' || persona === 'portfolio-manager') {
    return {
      summary: 'The CRE desk becomes a portfolio behavior screen where borrower quality, property cash flow, and refinance pathways outrank new production.',
      monitors: ['Yield durability by property and sponsor', 'Occupancy and NOI drift across the financed book', 'Refinance or distribution candidates by timing'],
      automations: ['Raise watchlist tasks when NOI or occupancy rolls over', 'Bundle distribution candidates into exit review packs', 'Keep surveillance notes attached to originating file context'],
      pulse: [pulse('Priority Lane', 'Portfolio', 'text-amber-300'), pulse('Execution Bias', 'Durability', 'text-emerald-400'), pulse('Coverage', 'Borrower + Asset', 'text-stone-300')],
    }
  }
  return {
    summary: `${ASSET_META[asset].label} activity through the CRE desk.`,
    monitors: ['Primary deal movement', 'Credit quality drift', 'Execution status'],
    automations: ['Refresh pipeline status', 'Update desk alerts', 'Publish action queue'],
    pulse: [pulse('Priority Lane', 'CRE', 'text-amber-300'), pulse('Execution Bias', 'Balanced', 'text-stone-300'), pulse('Coverage', 'Live', 'text-emerald-400')],
  }
}

function buildStructuredLens(moduleId: 'screening' | 'underwriting' | 'committee', persona: PersonaId): LensModule {
  const moduleLabel = MODULE_META[moduleId].label
  if (persona === 'secondary-trader') {
    return {
      summary: `${moduleLabel} now behaves like a trading surface: marks, dealer color, historical prints, and holder intelligence lead the page.`,
      monitors: ['Spread moves and executable liquidity', 'Inventory history and realized sale levels', 'Collateral drift that changes marks before headlines do'],
      automations: ['Trigger mark review when liquidity weakens or spread gaps widen', 'Attach historical trade context to current positions', 'Push inventory at risk into PM and reporting loops'],
      pulse: [pulse('Priority Lane', 'Price Action', 'text-amber-300'), pulse('Execution Bias', 'Trade', 'text-emerald-400'), pulse('Coverage', 'Street Flow', 'text-stone-300')],
    }
  }
  if (persona === 'structurer') {
    return {
      summary: `${moduleLabel} is organized for pool engineering, capital stack design, ratings readiness, and distribution timing.`,
      monitors: ['Collateral mix and execution timeline', 'Tranche economics and support levels', 'Investor or ratings feedback loops'],
      automations: ['Keep tapes, memos, and waterfalls in sync with the latest structure', 'Escalate timeline slips before pricing windows close', 'Refresh model overlays when collateral mix changes'],
      pulse: [pulse('Priority Lane', 'Structure', 'text-amber-300'), pulse('Execution Bias', 'Build', 'text-emerald-400'), pulse('Coverage', 'Ratings + Investors', 'text-stone-300')],
    }
  }
  if (persona === 'risk-manager') {
    return {
      summary: `${moduleLabel} is reframed around tail exposure, deterioration, and trigger headroom rather than just execution progress.`,
      monitors: ['Collateral performance and downgrade vectors', 'Structural trigger headroom or covenant pressure', 'Concentration and adverse scenario exposure'],
      automations: ['Escalate trigger erosion to watchlist and reports', 'Publish exception packs when structural thresholds tighten', 'Refresh stress scenarios on collateral drift'],
      pulse: [pulse('Priority Lane', 'Triggers', 'text-red-400'), pulse('Execution Bias', 'Protection', 'text-amber-300'), pulse('Coverage', 'Stress Paths', 'text-stone-300')],
    }
  }
  return {
    summary: `${moduleLabel} is positioned as an allocation engine balancing carry, liquidity, and structural resilience.`,
    monitors: ['Risk-adjusted carry by tranche or pool', 'Exit liquidity and spread cushion', 'Collateral behavior that can erode realized yield'],
    automations: ['Surface add/trim/rotate candidates', 'Push surveillance changes into PM reporting', 'Update relative-value comparisons in real time'],
    pulse: [pulse('Priority Lane', 'Allocation', 'text-amber-300'), pulse('Execution Bias', 'Carry', 'text-emerald-400'), pulse('Coverage', 'Liquidity', 'text-stone-300')],
  }
}

function buildAssetManagementLens(asset: FocusAssetId, persona: PersonaId): LensModule {
  if (persona === 'secondary-trader') {
    return {
      summary: 'Surveillance is shown here as a market input. What matters is which assets are likely to widen, gap lower, or lose bid depth first.',
      monitors: ['Watchlist names with tradable impact', 'Collateral events that change executable price', 'Borrower or property changes tied to mark revisions'],
      automations: ['Route deteriorating positions into mark review', 'Push event-driven alerts to the trading lens instantly', 'Snapshot realized sale history next to surveillance context'],
      pulse: [pulse('Priority Lane', 'Marks at Risk', 'text-red-400'), pulse('Execution Bias', 'Reactive', 'text-amber-300'), pulse('Coverage', 'Tradable Events', 'text-stone-300')],
    }
  }
  if (persona === 'originator') {
    return {
      summary: 'Surveillance becomes a feedback loop for new business. The main use is identifying what portfolio behavior should change future pricing and screening.',
      monitors: ['Recurring weak points by sponsor and property type', 'Triggers that should tighten credit posture', 'Assets trending toward refinance or rescue needs'],
      automations: ['Feed resolved portfolio lessons into new deal screening', 'Alert the origination desk on weak sponsors or sectors', 'Pre-build amendment or refinance packs from surveillance events'],
      pulse: [pulse('Priority Lane', 'Feedback Loop', 'text-amber-300'), pulse('Execution Bias', 'Learning', 'text-emerald-400'), pulse('Coverage', 'Origination Risk', 'text-stone-300')],
    }
  }
  return {
    summary: `${ASSET_META[asset].label} surveillance is promoted to protect carry, covenants, and downside across retained positions.`,
    monitors: ['Watchlists, triggers, and covenant pressure', 'Maturity and refinance readiness', 'Asset behavior that threatens duration or yield'],
    automations: ['Escalate material triggers into reports and action queues', 'Track servicing resolutions against SLA', 'Keep hold-versus-exit decisions tied to surveillance changes'],
    pulse: [pulse('Priority Lane', 'Watchlist', 'text-amber-300'), pulse('Execution Bias', 'Protect', 'text-emerald-400'), pulse('Coverage', 'Portfolio', 'text-stone-300')],
  }
}

function buildModelLens(asset: FocusAssetId, persona: PersonaId): LensModule {
  return {
    summary: `Model overlays are tuned to ${PERSONA_META[persona].label.toLowerCase()} decisions in ${ASSET_META[asset].label.toLowerCase()}: not just analytics, but defendable action support.`,
    monitors: ['Base-case versus market-source assumption divergence', 'Which variables are moving your decision most', 'Scenario sensitivity large enough to change risk or execution posture'],
    automations: ['Refresh model overlays when asset or persona lens changes', 'Attach assumption deltas to committee, PM, or risk output automatically', 'Flag decision zones where model disagreement becomes material'],
    pulse: [pulse('Priority Lane', 'Assumptions', 'text-amber-300'), pulse('Execution Bias', 'Decision Support', 'text-emerald-400'), pulse('Coverage', 'Cross-Model', 'text-stone-300')],
  }
}

function buildConversionLens(persona: PersonaId): LensModule {
  if (persona === 'secondary-trader') {
    return {
      summary: 'The conversion engine is interpreted as inventory aging, fallout opportunity, and hedge timing rather than a pure production funnel.',
      monitors: ['How fast positions become distributable', 'Where fallout creates purchase or sale opportunity', 'Whether hedges match current inventory risk'],
      automations: ['Escalate aged files into market-exit review', 'Refresh hedge coverage when exposure mix shifts', 'Highlight stalled positions with mark sensitivity'],
      pulse: [pulse('Priority Lane', 'Aging + Hedges', 'text-amber-300'), pulse('Execution Bias', 'Optionality', 'text-emerald-400'), pulse('Coverage', 'Inventory', 'text-stone-300')],
    }
  }
  if (persona === 'risk-manager') {
    return {
      summary: 'Velocity is used here to find control failures: slow transitions, unresolved conditions, and pipeline stages where risk accumulates quietly.',
      monitors: ['Stage bottlenecks and unresolved prerequisites', 'Where delays amplify risk or hedge cost', 'Pipeline segments with recurring fallout or exceptions'],
      automations: ['Escalate slow stages into control review', 'Route repeated fallout patterns into policy change queues', 'Refresh hedge or liquidity alerts on stalled execution'],
      pulse: [pulse('Priority Lane', 'Bottlenecks', 'text-red-400'), pulse('Execution Bias', 'Control', 'text-amber-300'), pulse('Coverage', 'Hedge Fit', 'text-stone-300')],
    }
  }
  return {
    summary: 'This module measures how reliably the desk turns pipeline into funded or distributable assets while keeping timing discipline.',
    monitors: ['Stage conversion and fallout', 'Average days lost at each handoff', 'Hedge coverage against current production exposure'],
    automations: ['Escalate slow stages automatically', 'Refresh handoff dashboards in real time', 'Tie hedge reviews to exposure shifts'],
    pulse: [pulse('Priority Lane', 'Execution', 'text-amber-300'), pulse('Execution Bias', 'Velocity', 'text-emerald-400'), pulse('Coverage', 'Pipeline', 'text-stone-300')],
  }
}

function buildReportLens(asset: FocusAssetId, persona: PersonaId): LensModule {
  return {
    summary: `Reporting is tailored to the ${PERSONA_META[persona].label.toLowerCase()} workflow in ${ASSET_META[asset].label.toLowerCase()}: committee, investor, risk, or trade context should be generated from the live operating state.`,
    monitors: ['Which report packs need to move with current desk priorities', 'Triggered communications required by surveillance or market events', 'Whether the current audience needs committee, investor, or control output first'],
    automations: ['Shift default report categories when the desk lens changes', 'Auto-populate packs from the active asset and persona context', 'Escalate event-triggered output without leaving the module workflow'],
    pulse: [pulse('Priority Lane', 'Audience Output', 'text-amber-300'), pulse('Execution Bias', 'Automated', 'text-emerald-400'), pulse('Coverage', 'Committee / Investor / Risk', 'text-stone-300')],
  }
}

export function getWorkbenchProfile(asset: FocusAssetId, persona: PersonaId): WorkbenchProfile {
  return pickPreset(asset, persona)
}

export function getModuleLens(asset: FocusAssetId, persona: PersonaId, moduleId: PlatformModuleId): LensModule {
  const preset = pickPreset(asset, persona)
  if (moduleId === 'command-center') return buildDashboardLens(asset, persona, preset)
  if (moduleId === 'intake') return buildCreLens(asset, persona)
  if (moduleId === 'screening' || moduleId === 'underwriting' || moduleId === 'committee') return buildStructuredLens(moduleId, persona)
  if (moduleId === 'asset-management') return buildAssetManagementLens(asset, persona)
  if (moduleId === 'market-intelligence') return buildModelLens(asset, persona)
  if (moduleId === 'closing') return buildConversionLens(persona)
  return buildReportLens(asset, persona)
}
